/**
 * Onboarding API Service
 *
 * Provides methods for the organisation admin to onboard new practitioners
 * (and other roles) via the backend `/admin/onboard-user` endpoint, and to
 * fetch the list of available roles from `/admin/roles`.
 *
 * The backend creates all the interconnected FHIR resources (User,
 * Practitioner, PractitionerRole, AccessPolicy, UserConfiguration,
 * ProjectMembership) in a single transaction.
 *
 * See `.prompts/architecture.md` for the current role and authorization model.
 */

import { apiClient, ApiError } from './client';
import { createComponentLogger } from '@/lib/utils/logger';
import type {
  OnboardUserRequest,
  OnboardUserResponse,
  GetRolesResponse,
  RoleOption,
} from '@/lib/types/onboarding';
import { DEFAULT_ROLE_OPTIONS } from '@/lib/types/onboarding';

const AUTH_API_BASE_URL = import.meta.env.VITE_AUTH_API_URL;

const logger = createComponentLogger('onboardingApi');

/**
 * Onboard a new user (practitioner or other role).
 *
 * Calls `POST /admin/onboard-user` on the backend. The backend creates
 * all required FHIR resources in a single transaction.
 *
 * @param payload - Onboarding request payload.
 * @returns The onboarding response with created resource IDs.
 * @throws {ApiError} - On request failure.
 */
export async function onboardUser(payload: OnboardUserRequest): Promise<OnboardUserResponse> {
  logger.log('Onboarding user:', payload.email, 'as', payload.role);

  try {
    const { data } = await apiClient.post<OnboardUserResponse>('/v2/admin/onboard-user', {
      baseUrl: AUTH_API_BASE_URL,
      body: payload,
    });
    logger.log('Onboarding successful:', data);
    return data;
  } catch (error) {
    const message =
      error instanceof ApiError
        ? error.message
        : error instanceof Error
          ? error.message
          : 'Failed to onboard user';
    logger.error('Onboarding failed:', message);
    throw error instanceof Error ? error : new Error(message);
  }
}

/**
 * Fetch the list of available roles from the backend.
 *
 * Calls `GET {VITE_AUTH_API_URL}/v2/admin/roles` through the shared API client.
 * Falls back to
 * `DEFAULT_ROLE_OPTIONS` if the endpoint is unavailable so the UI
 * remains usable.
 *
 * @returns Array of role options.
 */
export async function fetchRoles(): Promise<RoleOption[]> {
  try {
    const { data } = await apiClient.get<GetRolesResponse>('/v2/admin/roles', {
      baseUrl: AUTH_API_BASE_URL,
    });

    if (!data?.success || !Array.isArray(data.roles)) {
      return DEFAULT_ROLE_OPTIONS;
    }
    return data.roles;
  } catch (error) {
    logger.warn('Failed to fetch roles, using defaults:', error);
    return DEFAULT_ROLE_OPTIONS;
  }
}

/**
 * Update an existing user's details.
 *
 * Calls `PATCH /v2/admin/practitioner/{practitionerId}` on the backend, which
 * changes only the fields sent (the PUT on that path replaces the whole record
 * and clears anything left out). Updates the User, Practitioner, and related resources.
 *
 * @param practitionerId - The practitioner to update.
 * @param payload - Update payload (partial).
 * @returns The update response.
 * @throws {ApiError} - On request failure.
 */

export async function updateUser(
  practitionerId: string,
  payload: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    /** Replaces all specialties: the first is the primary (a catalogue code), the rest may be free text */
    specialty?: string[];
    role?: string;
  }
): Promise<{ success: boolean; message?: string }> {
  logger.log('Updating practitioner:', practitionerId, payload);

  try {
    const { data } = await apiClient.patch<{ success: boolean; message?: string }>(
      `/v2/admin/practitioner/${practitionerId}`,
      {
        baseUrl: AUTH_API_BASE_URL,
        body: payload,
      }
    );

    logger.log('Update successful:', data);
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to update practitioner';

    logger.error('Update failed:', message);
    throw error instanceof Error ? error : new Error(message);
  }
}

/**
 * Trigger the stale-record cleanup job manually.
 */
export async function triggerCronJob(date?: string): Promise<{
  success: boolean;
  result?: { encountersUpdated: number; appointmentsUpdated: number; errors: string[] };
}> {
  const { data } = await apiClient.post<{
    success: boolean;
    result?: { encountersUpdated: number; appointmentsUpdated: number; errors: string[] };
  }>('/v2/admin/jobs/close-stale-records', {
    baseUrl: AUTH_API_BASE_URL,
    body: date ? { date } : {},
  });
  return data;
}

/**
 * Deactivate a practitioner.
 */
export async function deactivateUser(
  practitionerId: string
): Promise<{ success: boolean; message?: string }> {
  logger.log('Deactivating practitioner:', practitionerId);

  try {
    const { data } = await apiClient.put<{ success: boolean; message?: string }>(
      `/v2/admin/practitioner/${practitionerId}/status`,
      {
        baseUrl: AUTH_API_BASE_URL,
        body: { active: false },
      }
    );
    logger.log('Deactivate successful:', data);
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to deactivate practitioner';
    logger.error('Deactivate failed:', message);
    throw error instanceof Error ? error : new Error(message);
  }
}

/**
 * Activate a practitioner.
 */
export async function activateUser(
  practitionerId: string
): Promise<{ success: boolean; message?: string }> {
  logger.log('Activating practitioner:', practitionerId);

  try {
    const { data } = await apiClient.put<{ success: boolean; message?: string }>(
      `/v2/admin/practitioner/${practitionerId}/status`,
      {
        baseUrl: AUTH_API_BASE_URL,
        body: { active: true },
      }
    );
    logger.log('Activate successful:', data);
    return data;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Failed to activate practitioner';
    logger.error('Activate failed:', message);
    throw error instanceof Error ? error : new Error(message);
  }
}

/**
 * Onboarding API surface.
 */
export const onboardingApi = {
  onboardUser,
  fetchRoles,
  updateUser,
  triggerCronJob,
  deactivateUser,
  activateUser,
};
